import { queryClient } from "@app/QueryClientWithHeaders";
import { GraphQLQuestionnaireRepository } from "@app/repositories/GraphQLRepositories/QuestionnaireRepository";
import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useMemo } from "react";
import { isAiProgressPopupOpenAtom } from "./ctx";

export const aiProgressKeys = {
    all: ["aiProgress"] as const,
    generatingQuestionnaireIds: () =>
        [...aiProgressKeys.all, "generatingQuestionnaireIds"] as const,
    popupQuestions: () => [...aiProgressKeys.all, "popupQuestions"] as const,
};

const externalQuestionnairesRepository = new GraphQLQuestionnaireRepository();

export const useGeneratingQuestionnaireIds = () => {
    const { data, ...query } = useQuery({
        queryKey: aiProgressKeys.generatingQuestionnaireIds(),
        queryFn: () =>
            externalQuestionnairesRepository.getGeneratingQuestionnaireIds(),
        staleTime: Infinity,
    });

    const generatingQuestionnaireIds = useMemo(() => {
        return data?.generatingQuestionnaireIds ?? [];
    }, [data]);

    return { generatingQuestionnaireIds, ...query };
};

export const useQuestions = (questionnaireIds: string[]) => {
    const { data, ...query } = useQuery({
        queryKey: aiProgressKeys.popupQuestions(),
        enabled: questionnaireIds.length > 0,
        queryFn: () =>
            externalQuestionnairesRepository.getPopupQuestions(
                questionnaireIds,
            ),
        refetchInterval: 1000,
    });

    const questions = useMemo(() => {
        const acceptableTimeToGenerate = 9000 * 10; // 90 seconds

        const questions = data?.popupQuestions ?? [];

        return questions.map((question) => {
            const isGenerating =
                !!question?.answer?.aiSuggestion?.isGenerating &&
                !!question?.answer?.lastGeneratedAt &&
                acceptableTimeToGenerate >
                    Date.now() -
                        new Date(question?.answer?.lastGeneratedAt).getTime();

            if (isGenerating) {
                return question;
            }
            return {
                ...question,
                answer: {
                    ...question.answer,
                    aiSuggestion: {
                        ...question.answer?.aiSuggestion,
                        isGenerating: false,
                    },
                },
            };
        });
    }, [data]);

    const suggestionsInProgress = useMemo(() => {
        return questions?.find(
            (question) => question.answer?.aiSuggestion?.isGenerating,
        );
    }, [questions]);

    const [isAiProgressPopupOpen, setIsAiProgressPopupOpen] = useAtom(
        isAiProgressPopupOpenAtom,
    );

    if (
        questionnaireIds.length > 0 &&
        questions.length > 0 &&
        !isAiProgressPopupOpen
    ) {
        setIsAiProgressPopupOpen(true);
    }

    if (questionnaireIds.length > 0 && !suggestionsInProgress) {
        queryClient.invalidateQueries({
            queryKey: aiProgressKeys.generatingQuestionnaireIds(),
        });
    }

    return { questions, ...query };
};
